<template>
  <div :class="['arena-indicator', { mobile: isMobile }]">
    <div v-for="arena in arenaList" :key="arena.id">
      <div :class="['arena-indicator-button-wrapper', arena.isHot ? 'hot-border' : '']">
        <div
          :class="[
            'arena-indicator-button',
            'inner',
            indicatorStatus(arena.fightStatus),
            { active: isArenaActive(arena) },
            { 'fight-started': isFightStarted(arena) },
            { 'fade-animation': shouldFadeAnimation(arena) },
          ]"
          @click="updateArena(arena)"
        >
          <div class="fight-bg"></div>

          <!-- NEW: left circle flag -->
          <div class="flag-pill">
            <div class="flag-ring">
              <img class="flag-img" :src="getFlagSrc(arena.country)" alt="flag" />
            </div>
          </div>

          <!-- Text -->
          <div class="arena-code">{{ arena.code }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep, take } from 'lodash';
import { isMobile } from 'mobile-device-detect';

export default {
  props: {
    align: {
      type: String,
      default: 'left',
    },
    size: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      startedFights: new Set(), // Track fights that have started
      fadedFights: new Set(), // Track fights that have completed fade animation
      isMobile,
    };
  },
  computed: {
    ...mapGetters('general', ['arenas', 'selectedArena']),
    ...mapGetters('taiXiu', ['activeProduct']),
    // In full Tài Xỉu mode the Tài Xỉu table is the active one, so no cockfight
    // arena is highlighted — but they all stay clickable to switch back.
    isTaiXiuActive() {
      return this.activeProduct === 'tai-xiu';
    },
    arenaList() {
      return cloneDeep(this.arenas).reverse();
    },
  },
  methods: {
    ...mapActions('general', ['updateGeneral']),
    ...mapActions('fight', ['resetFight', 'resetFights', 'resetFightsPage']),
    ...mapActions('taiXiu', ['selectCockfight']),

    isArenaActive(arena) {
      return !this.isTaiXiuActive && this.selectedArena.id === arena.id;
    },

    updateArena(arena) {
      const isSameArena = arena.id === this.selectedArena.id;

      // Coming from full Tài Xỉu mode: any arena tap switches back to cockfight,
      // even the one that is already selected.
      if (this.isTaiXiuActive) {
        this.selectCockfight();
      }

      if (isSameArena) return;

      this.updateGeneral({ selectedArena: { ...arena }, selectedArenaCode: arena.code });
      this.resetFights();
      this.resetFight();
      this.resetFightsPage();
    },

    isFightStarted(arena) {
      return this.startedFights.has(arena.id);
    },

    shouldFadeAnimation(arena) {
      return this.fadedFights.has(arena.id);
    },

    handleFightStarted(arenaId) {
      this.startedFights.add(arenaId);
      // Start fade animation after a brief delay
      setTimeout(() => {
        this.fadedFights.add(arenaId);
      }, 100);
      // Remove from faded after animation completes
      setTimeout(() => {
        this.fadedFights.delete(arenaId);
        this.startedFights.delete(arenaId);
      }, 3000);
    },

    handleFightStatus({ status, arenaId }) {
      if (status === 'started') {
        this.handleFightStarted(arenaId);
      }
    },

    indicatorStatus(status) {
      switch (status) {
        case 'draw':
          return 'green';
        case 'banker':
          return 'red';
        case 'player':
          return 'blue';
        case 'confirmed':
        case 'closing':
          return 'orange';
        default:
          return '';
      }
    },
    getFlagSrc(country) {
      try {
        return require(`@/assets/images/flags/${country}.webp`);
      } catch (e) {
        return 'data:image/webp;base64,UklGRlIAAABXRUJQVlA4IEYAAAAwAwCdASoUAA4APm0skkWkIqGYBABABsS0gAAt9Vmdj/IAAP77KW+67R0KYhmDrxRS2OF92nsnwKYkecnaCIIgrg84AAAA';
      }
    },
  },
  watch: {
    arenas: {
      deep: true,
      handler(newArenas, oldArenas) {
        if (!oldArenas) return;

        newArenas.forEach((arena) => {
          const oldArena = oldArenas.find((a) => a.id === arena.id);
          if (oldArena && oldArena.fightStatus !== 'started' && arena.fightStatus === 'started') {
            this.handleFightStarted(arena.id);
          }
        });
      },
    },
    // Watch for fight status changes from WebSocket
    '$store.state.fight.currentFightStatus': {
      handler(newStatus) {
        if (newStatus) {
          this.handleFightStatus(newStatus);
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes rotate {
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(360deg);
  }
}

.arena-indicator {
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;

  /* Wrapper for each button */
  .arena-indicator-button-wrapper {
    position: relative;
    margin: 0 6px 0 0;
    /* tighter spacing */
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    overflow: hidden;
    border-radius: 999px;
  }

  /* Rotating border when arena.isHot === true */
  .hot-border {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 0;

    &::before {
      content: '';
      display: block;
      background: linear-gradient(
        90deg,
        hsla(50, 100%, 50%, 1) 0%,
        hsla(33, 100%, 50%, 1) 40%,
        hsla(16, 100%, 50%, 1) 70%,
        hsla(0, 100%, 30%, 1) 100%
      );
      /* smaller halo so it fits the smaller pill */
      height: 88px;
      width: 88px;
      position: absolute;
      animation: rotate 1s linear infinite;
      z-index: -1;
      border-radius: 999px;
    }
  }

  /* Desktop positioning (your original behavior) */
  &:not(.mobile) {
    position: absolute;
    top: calc(-100% - 5px);
    left: 0;
    padding: 4px 0px;
    scrollbar-color: #1d2b38 #2e4c57;
    scrollbar-width: thin;
  }

  /* The button itself (pill + circle flag) */
  .arena-indicator-button {
    position: relative;
    overflow: hidden;

    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    /* smaller gap */

    border-radius: 999px;
    border: 2px solid rgba(190, 194, 214, 0.55);
    background: rgba(18, 26, 44, 0.85);

    padding: 0px 10px 0px 0px;
    margin: 1.2px;

    font-size: 14px;
    /* original-ish */
    font-weight: 800;
    /* slightly less heavy than 900 */
    letter-spacing: 0.02em;
    text-transform: uppercase;

    color: rgba(190, 194, 214, 0.95);

    transition: transform 0.08s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease,
      color 0.15s ease;

    /* The overlay you already use */
    .fight-bg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      background: url('~@/assets/images/start_game_icon.webp') center/contain no-repeat;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 5;
      opacity: 0;
      transition: opacity 3s ease-in, background-color 3s ease-in;
      pointer-events: none;
    }

    &.fight-started .fight-bg {
      opacity: 1;
    }

    &.fade-animation .fight-bg {
      opacity: 0;
      transition: opacity 3s ease-out, background-color 3s ease-out;
    }

    /* New left circle flag (smaller) */
    .flag-pill {
      position: relative;
      z-index: 4;
      flex: 0 0 auto;
      width: 18px;
      height: 18px;

      display: flex;
      align-items: center;
      justify-content: center;
      border-top: 2px solid rgba(190, 194, 214, 0.65);
      border-right: 2px solid rgba(190, 194, 214, 0.65);
      border-bottom: 2px solid rgba(190, 194, 214, 0.65);

      background: rgba(18, 26, 44, 0.85);

      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35) inset, 0 8px 14px rgba(0, 0, 0, 0.35);
      border-radius: 50%;
      padding: 4px;
    }

    .flag-ring {
      width: 18px;
      height: 18px;

      display: flex;
      align-items: center;
      justify-content: center;
    }

    .flag-img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }

    .arena-code {
      line-height: 1;
      display: flex;
      align-items: center;
    }

    &:hover {
      box-shadow: 0 0 10px rgba(255, 255, 255, 0.12);
    }

    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4) inset;
    }

    &.green {
      color: #0eb14d;
      border-color: #0eb14d;
      // background-color: rgba(14, 177, 77, 0.15);

      &.active {
        background-color: #0eb14d;
      }

      .flag-pill {
        border-color: #0eb14d;
      }
    }

    &.red {
      color: #e85050;
      border-color: #e85050;
      // background-color: rgba(232, 80, 80, 0.15);

      &.active {
        background-color: #e85050;
      }

      .flag-pill {
        border-color: #e85050;
      }
    }

    &.blue {
      color: #2890fa;
      border-color: #2890fa;
      // background-color: rgba(40, 144, 250, 0.15);

      &.active {
        background-color: #2890fa;
      }

      .flag-pill {
        border-color: #2890fa;
      }
    }

    &.orange {
      // border: 2px solid transparent;

      // background: linear-gradient(rgba(18, 26, 44, 0.85), rgba(18, 26, 44, 0.85)) padding-box,
      //   linear-gradient(90deg, #5c4b30, #bea171, #5c4b30, #bea171) border-box;

      // color: #b79a68;
      // /* optional: match gold text */

      // &.active {
      //   background: #b79a68;
      // }
      color: #ff8c38;
      border-color: #ff8c38;
      // background-color: rgba(255, 140, 56, 0.15);

      &.active {
        background: #ff8c38;
      }

      .flag-pill {
        // border: 2px solid transparent;

        // background: linear-gradient(rgba(18, 26, 44, 0.85), rgba(18, 26, 44, 0.85)) padding-box,
        //   linear-gradient(90deg, #5c4b30, #bea171, #5c4b30, #bea171) border-box;
        border-color: #ff8c38;
      }


      .flag-pill {
        // border: 2px solid transparent;

        // background: linear-gradient(rgba(18, 26, 44, 0.85), rgba(18, 26, 44, 0.85)) padding-box,
        //   linear-gradient(90deg, #5c4b30, #bea171, #5c4b30, #bea171) border-box;
        border-color: #ff8c38;
      }
    }

    &.active {
      color: #fff;
      cursor: default;
    }
  }

  /* Right-aligned variant from your original */
  &.right {
    justify-content: flex-end;
    top: 8px;
    right: 40px;
    left: unset;

    .arena-indicator-button-wrapper {
      margin: 0 0 0 6px;
    }
  }

  /* XS size option */
  &.xs .arena-indicator-button {
    font-size: 13px;
    padding: 3px 8px 3px 3px;

    .flag-pill,
    .flag-ring {
      width: 18px;
      height: 18px;
    }
  }
}

/* Mobile sizing (slightly smaller again) */
.arena-indicator.mobile {
  .arena-indicator-button {
    font-size: 13px;
    padding: 0px 8px 0px 0px;
    gap: 5px;

    .flag-pill,
    .flag-ring {
      width: 18px;
      height: 18px;
    }
  }

  .hot-border::before {
    height: 78px;
    width: 78px;
  }
}

@media screen and (min-width: 1200px) {
  .arena-indicator {
    .arena-indicator-button {
      font-size: 14px;
    }
  }
}
</style>
